// PortalSpawner.ts - Titan Beam + Portal Animation System

import { useGenesisState } from '../hooks/useGenesisState'

export interface PortalSpawnConfig {
  game: string
  color: string
  position?: [number, number, number]
}

// Titan beam state
let beamTimeout: ReturnType<typeof setTimeout> | null = null

// Trigger Titan's beam effect
export function triggerTitanBeam(): void {
  const { triggerTitanBeam: setBeamActive } = useGenesisState.getState()
  
  // Clear any existing timeout
  if (beamTimeout) {
    clearTimeout(beamTimeout)
  }
  
  // Activate beam
  setBeamActive()
  
  // Deactivate after animation duration
  beamTimeout = setTimeout(() => {
    setBeamActive()
  }, 1500)
}

// Parse portal spawn command from JSON
export function parsePortalCommand(jsonStr: string): PortalSpawnConfig | null {
  try {
    const parsed = JSON.parse(jsonStr)
    
    if (parsed.action === 'spawn_portal' && parsed.game && parsed.color) {
      return {
        game: parsed.game,
        color: parsed.color
      }
    }
  } catch {
    console.warn('Invalid portal command JSON')
  }
  return null
}

// Get game-specific portal position
export function getPortalPosition(game: string): [number, number, number] {
  const positions: Record<string, [number, number, number]> = {
    '2E3': [1.5, 1.5, -3],
    'TIME_FRACTURE': [0, 2.5, -4],
    'CHESS': [-1.5, 1.5, -3],
    'MESH_VISUALIZER': [0, 0.5, -2]
  }
  return positions[game] || [0, 2, -4]
}

// Get game portal color with variations
export function getPortalColor(game: string): string {
  const colors: Record<string, string> = {
    '2E3': '#FF00FF',
    'TIME_FRACTURE': '#00FFFF',
    'CHESS': '#8A2BE2',
    'MESH_VISUALIZER': '#10B981'
  }
  return colors[game] || '#FF00FF'
}

// Full portal spawn sequence
export async function spawnPortalSequence(
  game: string,
  color: string
): Promise<void> {
  const state = useGenesisState.getState()
  
  // Step 1: Titan beam flare
  triggerTitanBeam()
  
  // Step 2: Wait for beam animation
  await delay(800)
  
  // Step 3: Spawn portal
  state.spawnPortal(game, color)
  
  // Step 4: Dispatch event for WASM hot-swap
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('genesis:portal-spawned', {
      detail: { game, color }
    }))
  }
}

// Handle JSON tool event
export function handleJsonToolEvent(event: CustomEvent): void {
  const { action, game, color } = event.detail
  
  if (action === 'spawn_portal' && game && color) {
    spawnPortalSequence(game, color)
  }
}

// Initialize portal spawner listeners
export function initPortalSpawner(): () => void {
  if (typeof window === 'undefined') return () => {}
  
  const handleEvent = (event: Event) => {
    handleJsonToolEvent(event as CustomEvent)
  }
  
  window.addEventListener('genesis:json-tool', handleEvent)
  
  return () => {
    window.removeEventListener('genesis:json-tool', handleEvent)
  }
}

// Utility delay function
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Get portal label for display
export function getPortalLabel(game: string): string {
  const labels: Record<string, string> = {
    '2E3': 'Enter Social Space',
    'TIME_FRACTURE': 'Enter Time Fracture',
    'CHESS': 'Challenge Opponent',
    'MESH_VISUALIZER': 'View Mesh Topology'
  }
  return labels[game] || 'Enter Experience'
}
